import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { UserCircle } from 'lucide-react';
import { getImageUrl } from '../config/runtime';

/**
 * Animated notification for project events
 * Supports: join/leave, objective fulfillment, shout messages
 * Displays user avatar, name, action, and context info
 * Auto-dismisses after configured duration with smooth animations
 */
const ParticipantNotification = ({ user, action, objectiveTitle, objectiveProgress, shoutMessage, objective, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const onDismissRef = useRef(onDismiss);
  const dismissTimerRef = useRef(null);
  const finalizeTimerRef = useRef(null);
  const showRafRef = useRef(null);

  // Keep latest onDismiss without retriggering effects
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const displayName = user.firstName && user.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user.username;
  
  const location = [];
  if (user.location?.city) location.push(user.location.city);
  if (user.location?.country) location.push(user.location.country);
  const locationText = location.join(', ');

  // Determine colors, icons, and duration based on action
  const getActionConfig = () => {
    switch (action) {
      case 'joined':
        return {
          gradient: 'from-green-500 to-emerald-500',
          text: 'joined',
          icon: 'animate-pulse',
          emoji: null,
          duration: 5000
        };
      case 'left':
        return {
          gradient: 'from-orange-500 to-red-500',
          text: 'left',
          icon: 'animate-ping',
          emoji: null,
          duration: 5000
        };
      case 'objective_fulfilled':
        return {
          gradient: 'from-blue-500 via-purple-500 to-pink-500',
          text: 'fulfilled',
          icon: 'animate-bounce',
          emoji: '✨',
          duration: 6000
        };
        case 'objective_added':
            return {
              gradient: 'from-green-500 via-teal-500 to-blue-500',
              text: 'added',
              icon: 'animate-pulse',
              emoji: '🎯',
              duration: 5000
            };
        case 'objective_removed':
            return {
              gradient: 'from-red-500 via-pink-500 to-orange-500',
              text: 'removed',
              icon: 'animate-pulse',
              emoji: '🗑️',
              duration: 5000
            };
      case 'shout':
        return {
          gradient: 'from-purple-500 via-pink-500 to-rose-500',
          text: 'says',
          icon: 'animate-pulse',
          emoji: null,
          duration: 4000
        };
      default:
        return {
          gradient: 'from-gray-500 to-gray-600',
          text: action,
          icon: 'animate-pulse',
          emoji: null,
          duration: 5000
        };
    }
  };

  const config = getActionConfig();
  const isShout = action === 'shout';
  const containerBg = isShout 
    ? 'bg-white/90 dark:bg-gray-800/90 border border-purple-200 dark:border-purple-500/30'
    : `bg-gradient-to-r ${config.gradient}`;
  const textPrimary = isShout ? 'text-gray-900 dark:text-gray-100' : 'text-white';
  const textSecondary = isShout ? 'text-gray-700 dark:text-gray-300' : 'text-white/90';
  const textMuted = isShout ? 'text-gray-600 dark:text-gray-400' : 'text-white/70';
  const shoutMessageClass = isShout ? 'text-gray-900 dark:text-gray-100' : 'text-white/95';
  const avatarBorderClass = isShout ? 'border-purple-200 dark:border-purple-500/30' : 'border-white/30';
  const avatarIconClass = isShout ? 'text-purple-600 dark:text-purple-300' : 'text-white';
  const closeBtnBg = isShout 
    ? 'bg-gray-900/10 hover:bg-gray-900/20 dark:bg-white/10 dark:hover:bg-white/20'
    : 'bg-white/20 hover:bg-white/30';
  const closeBtnText = isShout ? 'text-gray-700 dark:text-gray-200' : 'text-white';

  useEffect(() => {
    // Slide in on next frame to allow transition
    showRafRef.current = requestAnimationFrame(() => setIsVisible(true));

    // Auto-dismiss based on action type
    dismissTimerRef.current = setTimeout(() => {
      setIsLeaving(true);
      finalizeTimerRef.current = setTimeout(() => {
        if (onDismissRef.current) onDismissRef.current();
      }, 300); // Wait for slide-out animation
    }, config.duration);

    return () => {
      if (showRafRef.current) cancelAnimationFrame(showRafRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      if (finalizeTimerRef.current) clearTimeout(finalizeTimerRef.current);
    };
  }, [action, config.duration]);

  const handleClickDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      if (onDismissRef.current) onDismissRef.current();
    }, 200);
  };

  const content = (
    <div className="fixed inset-0" style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
      <div
        className={`
          absolute left-1/2 -translate-x-1/2
          flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl cursor-pointer relative pr-6
          ${containerBg}
          backdrop-blur-lg
          transition-all duration-300 ease-out
          ${isVisible && !isLeaving ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'}
          min-w-[280px]
        `}
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          bottom: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'auto',
          maxWidth: 'min(420px, calc(100vw - 24px))',
          width: 'auto'
        }}
        role="button"
        tabIndex={0}
        onClick={handleClickDismiss}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClickDismiss(); }}
      >
      {/* Close button */}
      <button
        type="button"
        aria-label="Close notification"
        onClick={(e) => { e.stopPropagation(); handleClickDismiss(); }}
        className={`absolute top-1 right-1 w-6 h-6 rounded-full ${closeBtnBg} ${closeBtnText} flex items-center justify-center text-sm focus:outline-none`}
      >
        ×
      </button>
      {/* Avatar */}
      <div className="flex-shrink-0">
        {user.avatar ? (
          <img
            src={getImageUrl(user.avatar)}
            alt={displayName}
            className={`w-12 h-12 rounded-full border-2 ${avatarBorderClass} object-cover`}
          />
        ) : (
          <div className={`w-12 h-12 rounded-full border-2 ${avatarBorderClass} ${isShout ? 'bg-purple-50/60 dark:bg-purple-500/10' : 'bg-white/20'} flex items-center justify-center`}>
            <UserCircle className={`w-8 h-8 ${avatarIconClass}`} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 text-sm">
          <span className={`font-semibold ${textPrimary} truncate`}>
            {displayName}
          </span>
          <span className={`${textSecondary} font-medium`}>
            {config.text}
          </span>
        </div>
        
        {/* Context info: location for join/leave, objective for fulfillment, message for shout */}
        {action === 'shout' && shoutMessage ? (
          <div className={`text-sm ${shoutMessageClass} truncate mt-0.5 font-medium`}>
            "{shoutMessage}"
          </div>
        ) : action === 'objective_fulfilled' && objectiveTitle ? (
          <div className={`text-xs ${textSecondary} truncate mt-0.5 font-medium`}>
            🎯 {objectiveTitle}
            {objectiveProgress && ` (${objectiveProgress})`}
          </div>
        ) : action === 'objective_added' && objective?.objectiveName ? (
          <div className={`text-xs ${textSecondary} truncate mt-0.5 font-medium`}>
            🎯 {objective.objectiveName}
          </div>
        ) : action === 'objective_removed' && objective?.objectiveName ? (
          <div className={`text-xs ${textSecondary} truncate mt-0.5 font-medium`}>
            🗑️ {objective.objectiveName}
          </div>
        ) : locationText ? (
          <div className={`text-xs ${textMuted} truncate mt-0.5`}>
            📍 {locationText}
          </div>
        ) : null}
      </div>

      {/* Animated indicator */}
      {/* {config.emoji ? (
        <span className={`text-2xl ${config.icon}`}>{config.emoji}</span>
      ) : (
        <div className={`w-2 h-2 rounded-full bg-white ${config.icon}`} />
      )} */}
      </div>
    </div>
  );

  // Always render via portal to avoid being constrained by parent stacking contexts
  return ReactDOM.createPortal(content, document.body);
};

/**
 * Container that manages a queue of participant notifications
 * Shows them one at a time to avoid screen clutter
 */
export const ParticipantNotificationManager = ({ notifications, onConsumed }) => {
  const [activeNotification, setActiveNotification] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [queue, setQueue] = useState([]);
  const processedIdsRef = React.useRef(new Set());

  useEffect(() => {
    // Only process new notifications that haven't been shown yet
    const unprocessedNotifications = notifications.filter(
      notif => !processedIdsRef.current.has(notif.id)
    );

    if (unprocessedNotifications.length === 0) return;

    // Mark all new notifications as processed
    unprocessedNotifications.forEach(notif => {
      processedIdsRef.current.add(notif.id);
    });

    // Add unprocessed notifications to queue
    if (!activeNotification && unprocessedNotifications.length > 0) {
      // Show first unprocessed notification immediately
      setActiveNotification(unprocessedNotifications[0]);
      // Add rest to queue
      if (unprocessedNotifications.length > 1) {
        setQueue(prev => [...prev, ...unprocessedNotifications.slice(1)]);
      }
    } else {
      // Add all unprocessed to queue
      setQueue(prev => [...prev, ...unprocessedNotifications]);
    }
  }, [notifications, activeNotification]);

  const handleDismiss = () => {
    const dismissedId = activeNotification?.id;
    setActiveNotification(null);
    try {
      if (dismissedId && typeof onConsumed === 'function') {
        onConsumed(dismissedId);
      }
    } catch (_) {}
    // Show next in queue after a brief delay
    setTimeout(() => {
      setQueue(prev => {
        if (prev.length > 0) {
          setActiveNotification(prev[0]);
          return prev.slice(1);
        }
        return prev;
      });
    }, 100);
  };

  if (!activeNotification) return null;

  return (
    <ParticipantNotification
      user={activeNotification.user}
      action={activeNotification.action}
      objectiveTitle={activeNotification.objectiveTitle}
      objectiveProgress={activeNotification.objectiveProgress}
      shoutMessage={activeNotification.shoutMessage}
      objective={activeNotification.objective}
      onDismiss={handleDismiss}
    />
  );
};

export default ParticipantNotification;
